import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Profile } from '../types';
import { fetchProfileById } from '../services/profileService';
import { ProfileDetails } from '../../search';
import { useShortlistContext } from '../../../contexts/ShortlistContext';

const ProfileDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addProfileToShortlist, removeFromShortlist, isInShortlist } = useShortlistContext();

  useEffect(() => {
    if (!id) {
      setError('Profile ID is required');
      setLoading(false);
      return;
    }

    // Check if profile data was passed via navigation state
    const profileDataFromState = location.state?.profileData;
    if (profileDataFromState) {
      // Use the data from navigation state (no API call needed)
      setProfile(profileDataFromState);
      setLoading(false);
      return;
    }

    // If no data in state (e.g., page refresh), fetch from API
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const profileData = await fetchProfileById(id);
        setProfile(profileData);
      } catch (err) {
        setError('Failed to load profile');
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [id, location.state]);

  const handleBackToSearch = () => {
    // Pass the search state back to preserve search results and pagination
    const searchState = location.state?.searchState;
    navigate('/', { state: { searchState } });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--dreamdev-bg-primary)' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--dreamdev-bg-primary)' }}
      >
        <div className="text-center">
          <p style={{ color: '#EF4444', fontSize: '18px', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={handleBackToSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--dreamdev-bg-primary)' }}
      >
        <div className="text-center">
          <p style={{ color: '#6B7280', fontSize: '18px', marginBottom: '16px' }}>Profile not found</p>
          <button
            onClick={handleBackToSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={handleBackToSearch}
          className="mb-6 flex items-center text-blue-600 hover:text-blue-700 transition-colors"
          style={{ fontFamily: 'Source Sans 3, sans-serif', fontWeight: '600' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5 mr-2" />
          Back to Search
        </button>

        {/* Profile Content - Using reusable ProfileDetails component */}
        <div
          className="bg-white rounded-lg shadow-lg"
          style={{
            backgroundColor: '#FFFFFF',
            borderLeft: '1px solid #E5E7EB'
          }}
        >
          <ProfileDetails
            profile={profile}
            onAddToShortlist={(profileId) => {
              if (isInShortlist(profileId)) {
                removeFromShortlist(profileId);
              } else {
                addProfileToShortlist({
                  id: profile.id,
                  title: profile.candidateName || 'Software Engineer',
                  experience: `${Math.round((profile.experiences?.reduce((total, exp) => total + (exp.totalDurationMonths || 0), 0) || 0) / 12)} years of experience`,
                  skills: profile.experiences?.flatMap(exp => exp.roles?.flatMap(role => role.skills || []) || []) || [],
                  industries: profile.industries || [],
                });
              }
            }}
            isInShortlist={isInShortlist(profile.id)}
            showAddToShortlist={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailsPage;